import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { platformsData, platformSlugToApiName } from './platformsData';
import { Check, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useTrackingData } from './useTrackingData';
import { useTrackingStore } from '@/store/trackingStore';
import type { TrackingPlatform } from '@/services/trackingService';


interface PlatformSelectionProps {
  onConfirm: () => void;
  onBack: () => void;
}

export function PlatformSelection({
  onConfirm,
  onBack
}: PlatformSelectionProps) {
  useLanguage();
  const { data: trackingData, refetch } = useTrackingData();
  const [isConfirming, setIsConfirming] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const { selectedPlatforms, setSelectedPlatforms, addPendingPlatform } = useTrackingStore();
  const [localSelection, setLocalSelection] = useState<string[]>(
    selectedPlatforms.map(p => p.slug)
  );
  const [addAnother, setAddAnother] = useState<string[]>([]);

  const existingPlatformSlugs = selectedPlatforms.map(p => p.slug);

  const togglePlatform = (platformSlug: string) => {
    if (isConfirming) return;
    const platformName = platformsData.find(p => p.id === platformSlug)?.name || platformSlug;

    if (existingPlatformSlugs.includes(platformSlug)) {
      // Plataforma já conectada: alternar "adicionar outra integração"
      if (addAnother.includes(platformSlug)) {
        setAddAnother(addAnother.filter(p => p !== platformSlug));
      } else {
        setAddAnother([...addAnother, platformSlug]);
        toast.success(translateInterfaceText("Nova integração com {value0} será criada", { value0: (platformName) }), {
          position: "bottom-left",
          duration: 2000,
        });
      }
      return;
    }

    if (localSelection.includes(platformSlug)) {
      setLocalSelection(localSelection.filter(p => p !== platformSlug));
      toast.info(translateInterfaceText("{value0} removido da seleção", { value0: (platformName) }), {
        position: "bottom-left",
        duration: 2000,
      });
    } else {
      setLocalSelection([...localSelection, platformSlug]);
      toast.success(translateInterfaceText("Tracking para {value0} adicionado", { value0: (platformName) }), {
        position: "bottom-left",
        duration: 2000,
      });
    }
  };

  const mapToPlatform = (slug: string, platforms: TrackingPlatform[]) => {
    const apiName = platformSlugToApiName[slug] || slug;
    const apiPlatform = platforms.find(p => p.name === apiName);
    const localPlatform = platformsData.find(p => p.id === slug);
    if (apiPlatform?.id && localPlatform) {
      return { slug, platformId: apiPlatform.id, name: localPlatform.name };
    }
    return null;
  };

  const handleConfirm = async () => {
    if (isConfirming || (localSelection.length === 0 && addAnother.length === 0)) return;

    const newSlugs = localSelection.filter(slug => !existingPlatformSlugs.includes(slug));
    const requestedSlugs = [...newSlugs, ...addAnother];
    let platforms = trackingData?.plataforms || [];

    // A newly registered platform may be missing from the five-minute query cache.
    // Refresh before resolving IDs; never discard an unresolved selection silently.
    if (requestedSlugs.some(slug => !mapToPlatform(slug, platforms))) {
      setIsConfirming(true);
      try {
        const result = await refetch();
        if (!mountedRef.current) return;
        if (result.isError || !result.data?.plataforms) {
          toast.error(translateInterfaceText("Não foi possível carregar as plataformas. Tente novamente."));
          return;
        }
        platforms = result.data.plataforms;
      } catch {
        if (!mountedRef.current) return;
        toast.error(translateInterfaceText("Não foi possível carregar as plataformas. Tente novamente."));
        return;
      } finally {
        if (mountedRef.current) setIsConfirming(false);
      }
    }

    const unavailable = requestedSlugs.filter(slug => !mapToPlatform(slug, platforms));
    if (unavailable.length > 0) {
      const names = unavailable.map(slug => platformsData.find(p => p.id === slug)?.name || slug);
      toast.error(translateInterfaceText("{value0} indisponível neste ambiente. Tente novamente mais tarde.", { value0: (names.join(', ')) }));
      return;
    }

    const newPlatforms = newSlugs.map(slug => mapToPlatform(slug, platforms)!);
    const pendingPlatforms = addAnother.map(slug => mapToPlatform(slug, platforms)!);
    setSelectedPlatforms([
      ...selectedPlatforms.filter(p => localSelection.includes(p.slug)),
      ...newPlatforms,
    ]);
    pendingPlatforms.forEach(p => addPendingPlatform(p));
    onConfirm();
  };
  
  
  return (
    <div className="w-full flex flex-col items-start">
      <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-[14px] sm:gap-4 md:gap-5 mb-6 sm:mb-7 pb-4 sm:pb-0">
        {platformsData.map((platform) => {
          const isSelected = localSelection.includes(platform.id) && !existingPlatformSlugs.includes(platform.id);
          const isAddAnother = addAnother.includes(platform.id);

          return (
            <div
              key={platform.id}
              aria-disabled={isConfirming}
              className={`relative bg-white rounded-2xl transition-all duration-200 ease-in-out h-[70px] sm:h-[90px] md:h-[112px] w-full flex items-center justify-center cursor-pointer ${
                isSelected || isAddAnother
                  ? 'border-2 border-[#2563EB] shadow-[0_2px_8px_rgba(37,99,235,0.15)]'
                  : 'border border-[#E7EAF2]'
              }`}
              onClick={() => togglePlatform(platform.id)}
            >

              <div className="px-3 sm:px-4 flex items-center justify-center w-full h-full">
                <img
                  src={platform.logo}
                  alt={translateInterfaceText("{value0} logo", { value0: (platform.name) })}
                  className="max-w-full max-h-[36px] sm:max-h-[44px] md:max-h-[50px] w-auto object-contain opacity-95"
                />
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="fixed sm:relative bottom-0 left-0 right-0 sm:mt-0 bg-white p-4 pb-7 sm:p-0 sm:pb-0 border-t sm:border-0 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] sm:shadow-none z-50">
        <div className="flex items-center gap-[10px] w-full">
          <Button 
            variant="outline" 
            onClick={onBack}
            disabled={isConfirming}
            className="h-[46px] rounded-[10px] text-[15px] font-semibold bg-white border border-[#D1D5DB] text-[#374151] hover:border-[#2563EB] hover:text-[#FFFFFF] transition-all flex-1 sm:flex-initial"
          >
            {translateInterfaceText("Voltar")}</Button>
          <Button
            onClick={handleConfirm}
            disabled={isConfirming || (localSelection.length === 0 && addAnother.length === 0)}
            className="h-[46px] rounded-[10px] text-[15px] font-semibold bg-[#2563EB] text-white hover:bg-[#2563EB]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-[2] sm:flex-initial"
          >
            {isConfirming ? translateInterfaceText("Carregando plataformas...") : translateInterfaceText("Confirmar Plataformas")}
          </Button>
        </div>
      </div>
    </div>
  );
}
